import { getEmailValidationError } from './emailValidation';
import { getPhoneValidationError } from './phoneUtils';

/** Shared copy for simultaneous required-field validation feedback */
export const VALIDATION_SUMMARY_MESSAGE =
  'Please fix all highlighted required fields before saving.';

/** Messages set by real-time username/email/SSN/Tax ID availability checks */
export const AVAILABILITY_ERROR_MESSAGES = new Set([
  'This username is already taken. Please choose a different username.',
  'This email address is already registered. Please use a different email.',
  'This SSN is already registered to another patient. Please use a different SSN.',
  'Username is already in use.',
  'Email address is already in use.',
  'SSN is already in use.',
  'Admin username is already in use.',
  'Admin email address is already in use.',
  'This Tax ID is already registered to another practice.',
  'Tax ID is already in use.',
]);

/** Check if an error message represents a uniqueness/availability collision */
export function isAvailabilityErrorMessage(message) {
  if (!message || typeof message !== 'string') return false;
  if (AVAILABILITY_ERROR_MESSAGES.has(message)) return true;
  const lower = message.toLowerCase();
  return (
    (lower.includes('username') || lower.includes('email') || lower.includes('ssn') || lower.includes('tax id') || lower.includes('ein'))
    && (lower.includes('taken') || lower.includes('already') || lower.includes('in use') || lower.includes('registered'))
  );
}

/** True when any field error is from a username/email/SSN/Tax ID availability check */
export function hasAvailabilityConflict(errors = {}) {
  return Object.values(errors || {}).some((message) => isAvailabilityErrorMessage(message));
}

/** Keep API availability errors when re-running full form validation on submit */
export function preserveAvailabilityErrors(previousErrors, nextErrors) {
  const merged = { ...(nextErrors || {}) };
  if (!previousErrors) return merged;
  for (const [key, message] of Object.entries(previousErrors)) {
    if (isAvailabilityErrorMessage(message)) {
      merged[key] = message;
    }
  }
  return merged;
}

export const isBlank = (value) => {
  if (value === null || value === undefined) return true;
  return String(value).trim() === '';
};

/** Remove a single field error when the user edits that field */
export function clearFieldError(setErrors, fieldName) {
  setErrors((prev) => {
    if (!prev?.[fieldName]) return prev;
    const next = { ...prev };
    delete next[fieldName];
    return next;
  });
}

/**
 * Clear or set a field error using the latest errors state (avoids stale closure bugs).
 * Call after updating form data when the user selects/types a value.
 */
export function updateFieldErrorOnChange(setErrors, fieldName, value, { required = false, requiredMessage = null } = {}) {
  setErrors((prev) => {
    const next = { ...prev };
    const filled = String(value ?? '').trim() !== '';
    if (filled) {
      delete next[fieldName];
    } else if (required && requiredMessage) {
      next[fieldName] = requiredMessage;
    } else {
      delete next[fieldName];
    }
    return next;
  });
}

/** Remove multiple field errors in one update */
export function clearFieldErrors(setErrors, fieldNames = []) {
  setErrors((prev) => {
    const toClear = fieldNames.filter((fieldName) => prev?.[fieldName]);
    if (toClear.length === 0) return prev;
    const next = { ...prev };
    toClear.forEach((fieldName) => delete next[fieldName]);
    return next;
  });
}

/**
 * Apply all validation errors at once and optionally show a single summary toast.
 * @param {object} [previousErrors] - existing field errors (preserves availability conflicts)
 * @returns {boolean} true when the form is valid
 */
export function reportValidationErrors(setErrors, newErrors, toast, previousErrors = null) {
  const mergedErrors = previousErrors
    ? preserveAvailabilityErrors(previousErrors, newErrors)
    : newErrors;
  console.log('[reportValidationErrors] newErrors:', newErrors, 'previousErrors:', previousErrors, 'mergedErrors:', mergedErrors);
  setErrors(mergedErrors);
  const hasErrors = Object.keys(mergedErrors).length > 0;
  if (hasErrors && toast) {
    console.warn('[reportValidationErrors] Showing toast error! Failing keys:', Object.keys(mergedErrors), mergedErrors);
    toast.warn(VALIDATION_SUMMARY_MESSAGE);
  }
  return !hasErrors;
}

export const validateRequiredPhone = (value, { required = true, label = 'Phone' } = {}) =>
  getPhoneValidationError(value, { required, label });

export const validateRequiredEmail = (value) => getEmailValidationError(value, { required: true });
